import { useEffect, useRef, useState } from 'react'

import * as d3 from 'd3'
import { FormattedMessage } from 'react-intl'

import Theme from '@theme'
import { Text } from '@tribegroup'
import {
  ChartContainer,
  ChartTitle,
  EmptyCaption,
  SVGContainer,
  Tooltip,
} from './AudienceShareBarChart.styled'
import { IAudienceShareBarChartProps, IBarData } from './AudienceShareBarChart.types'

const AudienceShareBarChart: React.FC<IAudienceShareBarChartProps> = ({
  title,
  data,
  sort = true,
  hideZeroValues = true,
  tooltipLabel = 'Audience Share',
  chartHeight: chartHeightProp = 280,
  aspectRatio,
  barAreaRatio,
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0 })

  // Track if we should show empty state (no data, empty array, or all zeros)
  const showEmptyState =
    !data || data.length === 0 || data.every((d) => d.audienceSharePercentage === 0)

  // Responsive sizing using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries[0]) return
      const { width } = entries[0].contentRect
      setDimensions({ width })
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // D3 chart rendering (handles both data and empty states)
  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove() // Clear previous render

    // Total SVG height: responsive to width (aspectRatio, matching the chart.js siblings'
    // width ÷ aspectRatio rule) or a fixed chartHeight prop. The empty and data states share
    // these dimensions, so neither shifts the layout.
    const FIXED_TOP = 40
    const FIXED_BOTTOM = 80
    const MIN_BAR_AREA = 80
    const svgHeight = aspectRatio
      ? dimensions.width / aspectRatio
      : chartHeightProp + FIXED_TOP + FIXED_BOTTOM

    // barAreaRatio (0..1) sets how much of the height the bars occupy; the remainder is
    // split into top/bottom label padding. Without it, fixed label margins are used.
    let chartHeight
    let marginTop
    let marginBottom
    if (barAreaRatio) {
      // Desired padding from the ratio, but floor each side so the value numbers (top)
      // and category labels (bottom) always have room — otherwise a high ratio hides them.
      const remaining = svgHeight * (1 - barAreaRatio)
      marginTop = Math.max(remaining * 0.4, 8)
      marginBottom = Math.max(remaining * 0.6, 44)
      chartHeight = svgHeight - marginTop - marginBottom
    } else if (aspectRatio) {
      chartHeight = Math.max(svgHeight - FIXED_TOP - FIXED_BOTTOM, MIN_BAR_AREA)
      marginTop = FIXED_TOP
      marginBottom = FIXED_BOTTOM
    } else {
      chartHeight = chartHeightProp
      marginTop = FIXED_TOP
      marginBottom = FIXED_BOTTOM
    }
    const margin = { top: marginTop, right: 20, bottom: marginBottom, left: 20 }

    if (showEmptyState) {
      // Render the empty state at the same fixed pixel scale as the data state
      // (no viewBox stretching), so skeleton bar heights match real bars and the
      // chart doesn't shift height when data arrives.
      const chartWidth = dimensions.width - margin.left - margin.right

      svg
        .attr('width', dimensions.width)
        .attr('height', svgHeight)
        .attr('viewBox', null)
        .attr('preserveAspectRatio', null)
        .style('margin-bottom', null)

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

      // Heights are fractions of chartHeight so the skeleton scales with the bar area.
      const skeletonBars = [
        { fraction: 0.85, opacity: 0.5 },
        { fraction: 0.64, opacity: 0.4 },
        { fraction: 0.71, opacity: 0.45 },
        { fraction: 0.54, opacity: 0.35 },
        { fraction: 0.61, opacity: 0.4 },
      ].map((bar) => ({ height: chartHeight * bar.fraction, opacity: bar.opacity }))

      const numBars = skeletonBars.length
      const padding = 0.15
      const totalPaddingSpace = chartWidth * padding
      const paddingPerBar = totalPaddingSpace / numBars
      const barWidth = (chartWidth - totalPaddingSpace) / numBars

      skeletonBars.forEach((bar, index) => {
        const x = index * (barWidth + paddingPerBar) + paddingPerBar / 2
        const y = chartHeight - bar.height
        const barGroup = g.append('g')

        // Background track
        barGroup
          .append('rect')
          .attr('x', x)
          .attr('y', 0)
          .attr('width', barWidth)
          .attr('height', chartHeight)
          .attr('fill', Theme.grey300Color)
          .attr('opacity', 0.3)

        // Foreground bar
        barGroup
          .append('rect')
          .attr('x', x)
          .attr('y', y)
          .attr('width', barWidth)
          .attr('height', bar.height)
          .attr('fill', Theme.grey300Color)
          .attr('opacity', bar.opacity)
      })

      return
    }

    // Render data state. Zero-value filtering and value-sorting are opt-out via props
    // (e.g. credibility wants a fixed category order and keeps zero-value bars).
    const filteredData: IBarData[] = hideZeroValues
      ? data.filter((d) => d.audienceSharePercentage > 0)
      : [...data]
    const sortedData: IBarData[] = sort
      ? [...filteredData].sort((a, b) => b.audienceSharePercentage - a.audienceSharePercentage)
      : filteredData

    // Calculate chart width: ensure minimum bar width of 80px for readability
    // scaleBand padding formula: bandwidth = range / (n + padding * (n - 1) + 2 * outerPadding)
    // With padding(0.15): bandwidth = range / (n + 0.15*(n-1) + 2*0.15*0.5)
    // Simplified: requiredWidth = n * MIN_BAR_WIDTH / bandwidth_ratio
    const MIN_BAR_WIDTH = 80
    const PADDING = 0.15
    const n = sortedData.length
    const naturalChartWidth = dimensions.width - margin.left - margin.right
    const requiredWidth = n > 0 ? (n * MIN_BAR_WIDTH) / (1 - PADDING) + PADDING * MIN_BAR_WIDTH : 0
    const chartWidth = Math.max(naturalChartWidth, requiredWidth)
    const svgWidth = chartWidth + margin.left + margin.right
    const labelHeight = barAreaRatio ? Math.max(margin.bottom - 8, 16) : 60

    // Set SVG dimensions and clear empty state attributes
    svg
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .attr('viewBox', null) // Remove viewBox from empty state
      .attr('preserveAspectRatio', null) // Remove preserveAspectRatio from empty state
      .style('margin-bottom', null) // Remove margin-bottom from empty state

    // Create main group
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    // Scales
    // xScale: categorical scale for bar positioning (equal width bars)
    const xScale = d3
      .scaleBand()
      .domain(sortedData.map((d) => d.label))
      .range([0, chartWidth])
      .padding(0.15)

    // yScale: linear scale for percentage values [0, 1]
    // Inverted: 0 at bottom, 1 at top
    const yScale = d3.scaleLinear().domain([0, 1]).range([chartHeight, 0])

    // Create bar groups
    const barGroups = g
      .selectAll('g.bar-group')
      .data(sortedData)
      .join('g')
      .attr('class', 'bar-group')
      .attr('transform', (d) => `translate(${xScale(d.label)},0)`)
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        // Highlight bar
        d3.select(this).select('.foreground-bar').attr('opacity', 0.8)

        // Show tooltip
        const tooltip = d3.select(tooltipRef.current)
        tooltip
          .style('opacity', 1)
          .style('visibility', 'visible')
          .html(
            `<div style="font-weight: 600; margin-bottom: 4px;">${d.label}</div>` +
              `<div style="font-size: 12px;">${tooltipLabel}: <strong>${(
                d.audienceSharePercentage * 100
              ).toFixed(1)}%</strong></div>`,
          )
      })
      .on('mousemove', function (event) {
        // Position tooltip near cursor
        const tooltip = d3.select(tooltipRef.current)
        const containerRect = containerRef.current?.getBoundingClientRect()
        if (containerRect) {
          const x = event.clientX - containerRect.left + 10
          const y = event.clientY - containerRect.top - 10
          tooltip.style('left', `${x}px`).style('top', `${y}px`)
        }
      })
      .on('mouseout', function () {
        // Remove highlight
        d3.select(this).select('.foreground-bar').attr('opacity', 1)

        // Hide tooltip
        const tooltip = d3.select(tooltipRef.current)
        tooltip.style('opacity', 0).style('visibility', 'hidden')
      })

    const barWidth = xScale.bandwidth()

    // Background track (full height, primary100)
    barGroups
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', barWidth)
      .attr('height', chartHeight)
      .attr('fill', Theme.primary100Color)

    // Foreground bar (actual value)
    barGroups
      .append('rect')
      .attr('class', 'foreground-bar')
      .attr('x', 0)
      .attr('y', chartHeight) // Start at bottom for animation
      .attr('width', barWidth)
      .attr('height', 0) // Start at 0 for animation
      .attr('fill', Theme.primaryColor)
      .transition()
      .duration(600)
      .ease(d3.easeQuadOut)
      .attr('y', (d) => yScale(d.audienceSharePercentage))
      .attr('height', (d) => chartHeight - yScale(d.audienceSharePercentage))

    // Percentage labels (above bars)
    barGroups
      .append('text')
      .attr('x', barWidth / 2)
      .attr('y', (d) => yScale(d.audienceSharePercentage) - 12)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Rubik')
      .attr('font-size', '24px')
      .attr('font-weight', '400')
      .attr('letter-spacing', '0.3px')
      .attr('font-variant-numeric', 'tabular-nums')
      .attr('fill', Theme.primaryColor)
      .attr('opacity', 0)
      .text((d) => `${(d.audienceSharePercentage * 100).toFixed(1)}%`)
      .transition()
      .duration(600)
      .ease(d3.easeQuadOut)
      .attr('opacity', 1)

    // Category labels (below bars, max 3 lines with per-word ellipsis)
    barGroups
      .append('foreignObject')
      .attr('x', 0)
      .attr('y', chartHeight + 8)
      .attr('width', barWidth)
      .attr('height', labelHeight)
      .attr('opacity', 0)
      .each(function (d) {
        const fo = d3.select(this)
        const div = fo
          .append('xhtml:div')
          .style('width', '100%')
          .style('text-align', 'center')
          .style('font-family', 'Rubik')
          .style('font-size', '14px')
          .style('font-weight', '400')
          .style('color', '#808080')
          .style('line-height', '20px')
          .style('letter-spacing', '0.3px')
          .style('display', '-webkit-box')
          .style('-webkit-line-clamp', '3')
          .style('-webkit-box-orient', 'vertical')
          .style('overflow', 'hidden')

        // Wrap each word in an inline-block span so long words get ellipsis
        d.label.split(' ').forEach((word, i) => {
          if (i > 0) (div.node() as HTMLElement).appendChild(document.createTextNode(' '))
          div
            .append('xhtml:span')
            .style('display', 'inline-block')
            .style('max-width', '100%')
            .style('min-width', '0')
            .style('overflow', 'hidden')
            .style('text-overflow', 'ellipsis')
            .style('white-space', 'nowrap')
            .style('vertical-align', 'bottom')
            .text(word) // Use .text() to prevent XSS
        })
      })
      .transition()
      .duration(600)
      .ease(d3.easeQuadOut)
      .attr('opacity', 1)

    return () => {
      if (svgRef.current) {
        d3.select(svgRef.current).selectAll('*').remove()
      }
    }
  }, [
    data,
    dimensions,
    showEmptyState,
    sort,
    hideZeroValues,
    tooltipLabel,
    chartHeightProp,
    aspectRatio,
    barAreaRatio,
  ])

  return (
    <ChartContainer
      data-testid={showEmptyState ? 'audience-share-bar-chart-empty' : 'audience-share-bar-chart'}
    >
      {title && <ChartTitle>{title}</ChartTitle>}

      <SVGContainer ref={containerRef}>
        <svg ref={svgRef} />
        <Tooltip ref={tooltipRef} data-testid="bar-chart-tooltip" />
      </SVGContainer>

      {showEmptyState && (
        <EmptyCaption>
          <Text textAlign="center" color={Theme.grey700Color}>
            <FormattedMessage id="audienceInsights.chart.empty" />
          </Text>
        </EmptyCaption>
      )}
    </ChartContainer>
  )
}

export default AudienceShareBarChart
